import type { ReactElement, ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { GradedWork } from '../../../types';
import type { AuditVisibleRow } from '../../../lib/auditStudioDerived';
import { getSubmissionDisplayState } from '../../../lib/submissionDisplayState';
import { persistGradeHubSession } from '../../../lib/gradeHub/session';
import { useTrustAutosave } from '../../../hooks/useTrustAutosave';
import { auditRowToTrustState } from '../../trust/SubmissionStateBadge';
import { GradingActionStrip } from '../GradingActionStrip';
import { SubmissionViewShell } from '../SubmissionViewShell';
import { GradeQueuePanel } from './GradeQueuePanel';
import { GradeProgressHeader } from './GradeProgressHeader';
import { ReviewQueueLayout } from './ReviewQueueLayout';
import { GradingSidebar } from './GradingSidebar';
import { SubmissionViewer } from './SubmissionViewer';
import { useGradeViewerState } from './useGradeViewerState';

export type GradeReviewStudioProps = {
  courseId?: string;
  assignmentId?: string;
  courseName?: string;
  assignmentTitle?: string;
  rubricText?: string;
  commentTemplates: string[];
  visibleRows: AuditVisibleRow[];
  resolvedFocus: number | null;
  auditSelectedIndexes: Set<number>;
  onToggleBulk: (idx: number) => void;
  onSelectRow: (idx: number) => void;
  auditFilter: 'all' | 'errors' | 'drafts' | 'ready';
  onAuditFilter: (f: 'all' | 'errors' | 'drafts' | 'ready') => void;
  searchQuery: string;
  onSearchQuery: (q: string) => void;
  stats: {
    draft: number;
    ready: number;
    gradedComplete: number;
    total: number;
    ungraded: number;
  };
  draftScore: string;
  onDraftScoreChange: (s: string) => void;
  draftFeedback: string;
  onDraftFeedbackChange: (s: string) => void;
  onSaveDraft: () => void;
  onSaveAndNext: () => void;
  onFinalize: () => void;
  onRescan: (idx: number) => void;
  onDelete: (idx: number) => void;
  onAutosaveCommit: (index: number, scoreStr: string, feedback: string) => void;
  isOnline?: boolean;
  bulkFooterSlot: ReactNode;
  emptyQueueMessage: string;
  viewerLoading?: boolean;
};

function formatSecondaryLine(w: GradedWork, t: (k: string) => string): string {
  const hasPages = (w.imageUrls?.length ?? 0) > 0;
  const type = hasPages ? t('audit.gradeStudio.typePaper') : t('audit.gradeStudio.typeDigital');
  const date = w.timestamp
    ? new Date(w.timestamp).toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '';
  return date ? `${type} · ${date}` : type;
}

export function GradeReviewStudio({
  courseId,
  assignmentId,
  courseName,
  assignmentTitle,
  rubricText,
  commentTemplates,
  visibleRows,
  resolvedFocus,
  auditSelectedIndexes,
  onToggleBulk,
  onSelectRow,
  auditFilter,
  onAuditFilter,
  searchQuery,
  onSearchQuery,
  stats,
  draftScore,
  onDraftScoreChange,
  draftFeedback,
  onDraftFeedbackChange,
  onSaveDraft,
  onSaveAndNext,
  onFinalize,
  onRescan: _onRescan,
  onDelete: _onDelete,
  onAutosaveCommit,
  isOnline = true,
  bulkFooterSlot,
  emptyQueueMessage,
  viewerLoading = false,
}: GradeReviewStudioProps): ReactElement {
  const { t } = useTranslation();
  const viewerSliceRef = useRef({ pageIndex: 0, zoomPercent: 100, fitWidth: false });

  const persistSession = useCallback(() => {
    if (!courseId || !assignmentId) return;
    persistGradeHubSession({
      courseId,
      assignmentId,
      focusIndex: resolvedFocus,
      draftScore,
      draftFeedback,
      rubricText,
      viewerPageIndex: viewerSliceRef.current.pageIndex,
      viewerZoomPercent: viewerSliceRef.current.zoomPercent,
      viewerFitWidth: viewerSliceRef.current.fitWidth,
    });
  }, [courseId, assignmentId, resolvedFocus, draftScore, draftFeedback, rubricText]);

  const { status: autosaveStatus } = useTrustAutosave({
    enabled: resolvedFocus !== null,
    focusIndex: resolvedFocus,
    draftScore,
    draftFeedback,
    isOnline,
    onCommit: onAutosaveCommit,
    onAfterCommit: persistSession,
  });

  const viewer = useGradeViewerState({
    focusIdx: resolvedFocus,
    courseId,
    assignmentId,
    onViewerChange: (slice) => {
      viewerSliceRef.current = slice;
    },
  });

  const focusedWork = useMemo(
    () =>
      resolvedFocus !== null ? visibleRows.find((r) => r.idx === resolvedFocus)?.w : undefined,
    [resolvedFocus, visibleRows]
  );

  const focusedState = useMemo(
    () => visibleRows.find((r) => r.idx === resolvedFocus)?.state,
    [resolvedFocus, visibleRows]
  );

  const maxScore = focusedWork?.maxScore ?? 100;
  const imageUrls = focusedWork?.imageUrls ?? [];
  const submissionKind =
    imageUrls.length > 0
      ? ('paper' as const)
      : focusedWork
        ? ('digital' as const)
        : ('none' as const);

  const visibleOrder = useMemo(() => visibleRows.map((r) => r.idx), [visibleRows]);
  const focusPosition = resolvedFocus !== null ? visibleOrder.indexOf(resolvedFocus) : -1;
  const saveNextDisabled = focusPosition < 0 || focusPosition >= visibleOrder.length - 1;
  const previousDisabled = focusPosition <= 0;

  const queueRows = useMemo(
    () =>
      visibleRows.map(({ w, idx, state }) => ({
        key: idx,
        studentName: w.studentName || t('audit.row.unknownStudent'),
        secondaryLine: formatSecondaryLine(w, t),
        state,
        displayState: getSubmissionDisplayState(w, state),
        synced: w.status === 'synced',
        score: w.score,
        maxScore: w.maxScore,
        focused: resolvedFocus === idx,
        bulkSelected: auditSelectedIndexes.has(idx),
        onToggleBulk: () => onToggleBulk(idx),
        onSelect: () => onSelectRow(idx),
      })),
    [visibleRows, resolvedFocus, auditSelectedIndexes, onToggleBulk, onSelectRow, t]
  );

  const focusAdjacent = useCallback(
    (delta: number) => {
      if (visibleOrder.length === 0) return;
      const i = focusPosition >= 0 ? focusPosition : 0;
      const next = Math.max(0, Math.min(visibleOrder.length - 1, i + delta));
      const idx = visibleOrder[next];
      if (idx !== undefined) onSelectRow(idx);
    },
    [visibleOrder, focusPosition, onSelectRow]
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      const inField = tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';

      if (e.key === 'Enter' && !e.shiftKey && !inField) {
        e.preventDefault();
        onSaveAndNext();
        return;
      }

      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        if (inField && !(e.metaKey || e.ctrlKey)) return;
        e.preventDefault();
        focusAdjacent(e.key === 'ArrowDown' ? 1 : -1);
        return;
      }

      if ((e.key === 's' || e.key === 'S') && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        onSaveDraft();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [focusAdjacent, onSaveDraft, onSaveAndNext]);

  const onInsertComment = useCallback(
    (text: string) => {
      onDraftFeedbackChange(draftFeedback ? `${draftFeedback}\n${text}` : text);
    },
    [draftFeedback, onDraftFeedbackChange]
  );

  const onRubricCriterionSelect = useCallback(
    (line: string) => {
      onDraftFeedbackChange(draftFeedback ? `${draftFeedback}\n${line}` : line);
    },
    [draftFeedback, onDraftFeedbackChange]
  );

  const trustState = focusedState !== undefined ? auditRowToTrustState(focusedState) : null;

  const leftPanel = (
    <GradeQueuePanel
      courseLabel={courseName}
      assignmentLabel={assignmentTitle}
      searchQuery={searchQuery}
      onSearchQuery={onSearchQuery}
      auditFilter={auditFilter}
      onAuditFilter={onAuditFilter}
      gradedCount={stats.gradedComplete}
      totalCount={stats.total}
      ungradedCount={stats.ungraded}
      draftCount={stats.draft}
      readyCount={stats.ready}
      rows={queueRows}
      emptyMessage={emptyQueueMessage}
      footerSlot={bulkFooterSlot}
      className="lg:border-0 lg:border-r-0 lg:bg-transparent lg:p-0 dark:lg:bg-transparent"
    />
  );

  const centerPanel =
    visibleRows.length === 0 ? (
      <div className="flex flex-1 items-center justify-center p-8 text-dg-body text-slate-500 dark:text-slate-400">
        {emptyQueueMessage}
      </div>
    ) : focusedWork && resolvedFocus !== null ? (
      <SubmissionViewer
        loading={viewerLoading}
        studentName={focusedWork.studentName || t('audit.row.unknownStudent')}
        assignmentTitle={assignmentTitle}
        imageUrls={imageUrls}
        kind={submissionKind}
        pageIndex={viewer.pageIndex}
        onPageIndex={viewer.setPageIndex}
        zoomPercent={viewer.zoomPercent}
        onZoomIn={viewer.onZoomIn}
        onZoomOut={viewer.onZoomOut}
        rotationDeg={viewer.rotationDeg}
        onRotate={viewer.onRotate}
        fitWidth={viewer.fitWidth}
        onToggleFitWidth={() => viewer.setFitWidth((f) => !f)}
        annotateEnabled={false}
        autosaveStatus={autosaveStatus}
        submissionTrustState={trustState}
        submissionSynced={focusedWork.status === 'synced'}
        emptyLabel={t('audit.gradeStudio.viewerEmpty')}
        className="lg:border-0 lg:rounded-none lg:shadow-none dark:lg:border-slate-700/70"
      />
    ) : (
      <div className="flex flex-1 items-center justify-center p-8 text-dg-body text-slate-500 dark:text-slate-400">
        {t('audit.gradeStudio.pickStudent')}
      </div>
    );

  const gradingPanel =
    visibleRows.length === 0 || resolvedFocus === null || !focusedWork ? (
      <div className="flex min-h-[200px] flex-1 items-center justify-center p-4 text-center text-dg-secondary text-slate-500 dark:text-slate-400">
        {t('audit.gradeStudio.sidebarEmpty')}
      </div>
    ) : (
      <GradingSidebar
        rubricText={rubricText}
        scoreValue={draftScore}
        onScoreChange={onDraftScoreChange}
        maxScore={maxScore}
        feedbackValue={draftFeedback}
        onFeedbackChange={onDraftFeedbackChange}
        commentTemplates={commentTemplates}
        onInsertComment={onInsertComment}
        onRubricCriterionSelect={onRubricCriterionSelect}
      />
    );

  const mobileQueueStrip = (
    <div className="shrink-0 border-b border-dg-border p-3 dark:border-slate-700/70">
      <GradeProgressHeader
        gradedCount={stats.gradedComplete}
        totalCount={stats.total}
        ungradedCount={stats.ungraded}
        draftCount={stats.draft}
        readyCount={stats.ready}
        compact
      />
      <ul className="mt-2 flex max-h-28 flex-col gap-1.5 overflow-y-auto custom-scrollbar">
        {queueRows.map((r) => (
          <li key={r.key}>
            <button
              type="button"
              onClick={r.onSelect}
              className={`w-full truncate rounded-dg-control border px-3 py-2 text-left text-dg-body font-medium ${
                r.focused
                  ? 'border-dg-accent-grade bg-dg-accent-gradeLight text-amber-950 dark:border-amber-600 dark:bg-amber-950/35 dark:text-amber-100'
                  : 'border-slate-200/80 bg-white text-slate-800 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100'
              }`}
            >
              {r.studentName}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );

  const mobileWorkPanel = (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      {mobileQueueStrip}
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">{centerPanel}</div>
    </div>
  );

  const mobileRubricPanel =
    resolvedFocus !== null && focusedWork ? (
      <GradingSidebar
        rubricText={rubricText}
        scoreValue={draftScore}
        onScoreChange={onDraftScoreChange}
        maxScore={maxScore}
        feedbackValue={draftFeedback}
        onFeedbackChange={onDraftFeedbackChange}
        commentTemplates={[]}
        onInsertComment={onInsertComment}
        onRubricCriterionSelect={onRubricCriterionSelect}
        sections="rubric"
      />
    ) : (
      gradingPanel
    );

  const mobileFeedbackPanel =
    resolvedFocus !== null && focusedWork ? (
      <GradingSidebar
        rubricText={rubricText}
        scoreValue={draftScore}
        onScoreChange={onDraftScoreChange}
        maxScore={maxScore}
        feedbackValue={draftFeedback}
        onFeedbackChange={onDraftFeedbackChange}
        commentTemplates={commentTemplates}
        onInsertComment={onInsertComment}
        sections="feedback"
      />
    ) : (
      gradingPanel
    );

  const actionStrip = (
    <GradingActionStrip
      onSaveDraft={onSaveDraft}
      onSaveAndNext={onSaveAndNext}
      onFinalize={onFinalize}
      onPrevious={() => focusAdjacent(-1)}
      previousDisabled={previousDisabled}
      saveNextDisabled={saveNextDisabled}
      autosaveStatus={autosaveStatus}
    />
  );

  return (
    <SubmissionViewShell
      progressHeader={
        <GradeProgressHeader
          gradedCount={stats.gradedComplete}
          totalCount={stats.total}
          ungradedCount={stats.ungraded}
          draftCount={stats.draft}
          readyCount={stats.ready}
          compact
        />
      }
      actionStrip={actionStrip}
    >
      <ReviewQueueLayout
        leftPanel={leftPanel}
        centerPanel={centerPanel}
        rightPanel={gradingPanel}
        mobileWorkPanel={mobileWorkPanel}
        mobileRubricPanel={mobileRubricPanel}
        mobileFeedbackPanel={mobileFeedbackPanel}
      />
    </SubmissionViewShell>
  );
}
